import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from 'react';
import { Sort } from "@models";
import { MenuItemExample } from "@apiHooks/useMenuExamplesPopupData";

const BYO_MENU_EXAMPLES_ENDPOINT: keyof paths = '/ByoMenuExamples';

type ByoMenuExamplesRequest = components['schemas']['ByoMenuExamplesFilterInput'];
type ByoMenuExamplesResponse = components['schemas']['MenuExamplesOutputModel'];
type MainFilterInput = components['schemas']['ByoMenuExampleFilterInput'];
type TableParameters = components["schemas"]["ByoMenuExamplesSortParameters"];
export type ByoMenuExamplesExportRequest = components['schemas']['ByoMenuExampleExportFilter'];
export type SliceParameter = components['schemas']['Slice'];

type FetchOptions = {
    sortColumn: number;
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

export interface UseByoMenuExamplesParams {
    word: MainFilterInput['word'] | null;
    filters: MainFilterInput['filters'];
    fullFilter: ByoMenuExamplesExportRequest['fullFilter'];
    sliceParameter: SliceParameter | null;
}

export default function useByoMenuExamples({
    word,
    filters,
    fullFilter,
    sliceParameter,
}: UseByoMenuExamplesParams) {
    const {
        doFetch,
        cancel: cancelFetch,
    } = useAxios<ByoMenuExamplesResponse, ByoMenuExamplesRequest>({
        method: 'post',
        url: BYO_MENU_EXAMPLES_ENDPOINT,
        lazy: true,
    });

    const fetch = useCallback(
        async (options: FetchOptions): Promise<FetchResponse> => {
            if (!word) {
                return { data: [], totalRecords: 0 };
            }

            const tableParameters: TableParameters = {
                sortColumn: options.sortColumn as TableParameters['sortColumn'],
                search: options.search,
                take: options.take,
                skip: options.skip,
                sortDir: options.sortDir === Sort.Asc ? 0 : 1,
            };

            const requestData: ByoMenuExamplesRequest = {
                mainFilter: {
                    word,
                    filters,
                },
                sliceParameter: sliceParameter ?? undefined,
                tableParameters,
            };

            const response = await doFetch({ data: requestData });

            return {
                data: response?.data?.data ?? [],
                totalRecords: response?.data?.totalRecords ?? 0,
            };
        },
        [word, filters, sliceParameter, doFetch]
    );

    const getExportData = useCallback((options: FetchOptions) => {
        if (!word) return;

        const tableParameters: TableParameters = {
            sortColumn: options.sortColumn as TableParameters['sortColumn'],
            search: options.search,
            take: options.take,
            skip: options.skip,
            sortDir: options.sortDir === Sort.Asc ? 0 : 1,
        };

        return {
            fullFilter,
            customerFilter: {
                mainFilter: {
                    word,
                    filters,
                },
                sliceParameter: sliceParameter ?? undefined,
                tableParameters,
            },
        };
    }, [word, filters, fullFilter, sliceParameter]);

    const cancel = useCallback(() => {
        cancelFetch();
    }, [cancelFetch]);

    const isReady = useMemo(() => !!word, [word]);

    return {
        fetch,
        getExportData,
        cancel,
        isReady,
    };
}
